import React from 'react';
import { Text, View, StyleSheet } from '@react-pdf/renderer';
import { calculateInvoicingValues } from 'components/modules/EditHarvestSummaryModal/utils';
import { formatCurrency } from 'utils/formatNumbers';

const styles = StyleSheet.create({
  notesNdTotal: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    width: '100%',
    marginTop: 8, // Reduced from 16 to give more space for footer
  },
  itemDetails: {
    width: 'auto',
    minWidth: 200,
    maxWidth: '100%',
    backgroundColor: '#FFFCE8',
    borderRadius: 12,
    fontSize: 10,
    color: '#53575A',
    fontFamily: 'Trebuchet',
    fontWeight: 'bold',
    lineHeight: 1.5,
    padding: 4, // Further reduced to give maximum space for footer
    alignSelf: 'flex-end',
  },
  impactTextTitileBtm: {
    marginBottom: 6, // Reduced from 10 to give more space for footer
    color: '#53575A',
    fontSize: 16,
    fontWeight: 'bold',
    textAlign: 'left',
  },
  totalRow: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    justifyContent: 'space-between',
  },
  totalColTitle: {
    textAlign: 'left',
    flex: '0 0 auto',
  },
  totalColValue: {
    textAlign: 'right',
    flex: '0 0 auto',
    minWidth: 0,
  },
  totalValueTight: {
    fontSize: 10,
    lineHeight: 1.5,
  },
});

type PdfHarvestSummaryReportProps = {
  reportData: any;
  break?: boolean;
};

const NotesNdTotal = ({
  reportData,
  break: shouldBreak,
}: PdfHarvestSummaryReportProps) => {
  const values = calculateInvoicingValues(
    reportData?.details,
    reportData?.site,
    reportData?.reportType,
  );
  const cokeAdjustment = reportData?.cokeAdjustment ?? 0;
  const feeAdjustment = reportData?.feeAdjustment ?? 0;
  // Typed fee rows (with a fee-type label each); legacy reports only carry the
  // scalar feeAdjustment and render as a single "Fee/Adjustments" line.
  const feeAdjustments: Array<{ type: string; amount: number }> =
    reportData?.feeAdjustments?.length > 0
      ? reportData.feeAdjustments
      : feeAdjustment !== 0
      ? [{ type: 'Fee/Adjustments', amount: feeAdjustment }]
      : [];
  const noAdjustments = cokeAdjustment === 0 && feeAdjustments.length === 0;

  return (
    <View style={styles.notesNdTotal} break={shouldBreak}>
      <View style={styles.itemDetails}>
        <Text style={styles.impactTextTitileBtm}>Totals</Text>

        {!noAdjustments && (
          <View style={styles.totalRow}>
            <Text style={styles.totalColTitle}>Subtotal</Text>
            <Text style={styles.totalColValue}>
              {formatCurrency(values.subtotal)}
            </Text>
          </View>
        )}

        {reportData?.site.hasCokeAdjustment && cokeAdjustment > 0 && (
          <View style={styles.totalRow}>
            <Text style={styles.totalColTitle}>Coke Adjustment</Text>
            <Text style={styles.totalColValue}>
              -{formatCurrency(cokeAdjustment)}
            </Text>
          </View>
        )}

        {feeAdjustments.map((fee, index) => (
          <View style={styles.totalRow} key={`${fee.type}-${index}`}>
            <Text style={styles.totalColTitle}>{fee.type}</Text>
            <Text style={styles.totalColValue}>
              {formatCurrency(fee.amount)}
            </Text>
          </View>
        ))}

        <View style={styles.totalRow}>
          <Text style={[styles.totalColTitle, { fontWeight: 'bold' }]}>
            Total
          </Text>
          <Text
            style={[
              styles.totalColValue,
              ...(values.subtotal - cokeAdjustment + feeAdjustment > 99999999
                ? [styles.totalValueTight]
                : []),
            ]}>
            {formatCurrency(values.subtotal - cokeAdjustment + feeAdjustment)}
          </Text>
        </View>
      </View>
    </View>
  );
};

export default NotesNdTotal;
